/**
 * Todas las llamadas a la API, en un solo sitio.
 *
 * Las pantallas no llaman a `fetch` directamente: usan estas funciones. Así el
 * contrato se cumple en un único lugar y no hay forma de que una pantalla se
 * invente un campo con otro nombre, que es exactamente lo que hacía que en
 * Cuentia se perdieran datos sin avisar.
 */

import { z } from 'zod';

import type {
  Account,
  AccountCreate,
  AccountUpdate,
  Budget,
  BudgetPacing,
  BudgetUpsert,
  Category,
  CategoryCreate,
  Debt,
  DebtCreate,
  DebtPaymentCreate,
  DebtUpdate,
  FiadoEntry,
  FiadoStore,
  FiadoStoreCreate,
  FiadoStoreUpdate,
  Goal,
  GoalContributionCreate,
  GoalCreate,
  GoalUpdate,
  Paginated,
  RecurringCreate,
  RecurringRule,
  RecurringUpdate,
  Settings,
  SettingsUpdate,
  Split,
  SplitCreate,
  SplitSettle,
  SyncOperationResult,
  SyncPullResult,
  UserPublic,
  Transaction,
  TransactionCreate,
  TransactionFilter,
  TransactionUpdate,
} from '@eldescuadre/contracts';

import { request } from './api';

// ---------------------------------------------------------------------------
//  Categorías y cuentas
// ---------------------------------------------------------------------------

export const getCategories = (kind?: 'EXPENSE' | 'INCOME'): Promise<{ categories: Category[] }> =>
  request('/api/categories', { query: kind ? { kind } : undefined });

export const createCategory = (input: CategoryCreate): Promise<{ category: Category }> =>
  request('/api/categories', { method: 'POST', body: input });

export const updateCategory = (
  id: string,
  input: Partial<CategoryCreate> & { isActive?: boolean },
): Promise<{ category: Category }> =>
  request(`/api/categories/${id}`, { method: 'PUT', body: input });

export const deleteCategory = (id: string): Promise<{ ok: true }> =>
  request(`/api/categories/${id}`, { method: 'DELETE' });

export const getAccounts = (includeArchived = false): Promise<{ accounts: Account[] }> =>
  request('/api/accounts', { query: includeArchived ? { includeArchived: 'true' } : undefined });

/**
 * Crea una cuenta.
 *
 * Los valores por defecto del contrato (moneda, color, icono y orden) viajan aquí
 * para que las pantallas no tengan que repetirlos en cada formulario. El servidor
 * los aplicaría igual.
 */
export const createAccount = (input: AccountCreate): Promise<{ account: Account }> =>
  request('/api/accounts', {
    method: 'POST',
    body: { currency: 'COP', color: '#20A9E6', icon: 'Wallet', order: 0, ...input },
  });

export const updateAccount = (id: string, input: AccountUpdate): Promise<{ account: Account }> =>
  request(`/api/accounts/${id}`, { method: 'PUT', body: input });

export const deleteAccount = (
  id: string,
): Promise<{ ok: true; archived: boolean; message: string }> =>
  request(`/api/accounts/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
//  Movimientos
// ---------------------------------------------------------------------------

/** Convierte los filtros a parámetros de la barra de direcciones. */
function transactionQuery(filter: Partial<TransactionFilter>): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (filter.from) query.from = filter.from;
  if (filter.to) query.to = filter.to;
  if (filter.monthKey) query.monthKey = filter.monthKey;
  if (filter.kinds?.length) query.kinds = filter.kinds.join(',');
  if (filter.categoryIds?.length) query.categoryIds = filter.categoryIds.join(',');
  if (filter.accountIds?.length) query.accountIds = filter.accountIds.join(',');
  if (filter.tagIds?.length) query.tagIds = filter.tagIds.join(',');
  if (filter.search) query.search = filter.search;
  if (filter.minAmount !== undefined) query.minAmount = filter.minAmount;
  if (filter.maxAmount !== undefined) query.maxAmount = filter.maxAmount;
  if (filter.includeDeleted) query.includeDeleted = 'true';
  if (filter.page !== undefined) query.page = filter.page;
  if (filter.pageSize !== undefined) query.pageSize = filter.pageSize;
  return query;
}

export const getTransactions = (
  filter: Partial<TransactionFilter> = {},
): Promise<Paginated<Transaction>> =>
  request('/api/transactions', { query: transactionQuery(filter) });

export const getTransaction = (id: string): Promise<{ transaction: Transaction }> =>
  request(`/api/transactions/${id}`);

/**
 * Crea un movimiento.
 *
 * La clave de idempotencia viaja **en la cabecera**, que es lo que envía la cola
 * de sincronización cuando el móvil se queda sin señal. En Cuentia el servidor
 * solo miraba el cuerpo y el reintento duplicaba el gasto.
 */
export const createTransaction = (
  input: TransactionCreate,
  idempotencyKey?: string,
): Promise<{ transaction: Transaction; idempotent: boolean }> =>
  request('/api/transactions', {
    method: 'POST',
    body: input,
    ...(idempotencyKey ? { headers: { 'idempotency-key': idempotencyKey } } : {}),
  });

export const updateTransaction = (
  id: string,
  input: TransactionUpdate,
): Promise<{ transaction: Transaction }> =>
  request(`/api/transactions/${id}`, { method: 'PUT', body: input });

export const deleteTransaction = (id: string): Promise<{ ok: true }> =>
  request(`/api/transactions/${id}`, { method: 'DELETE' });

export const restoreTransaction = (id: string): Promise<{ transaction: Transaction }> =>
  request(`/api/transactions/${id}/restore`, { method: 'POST' });

// ---------------------------------------------------------------------------
//  Presupuestos
// ---------------------------------------------------------------------------

export const getBudgets = (monthKey?: string): Promise<{ monthKey: string; budgets: Budget[] }> =>
  request('/api/budgets', { query: monthKey ? { monthKey } : undefined });

export const getBudgetPacing = (
  monthKey?: string,
): Promise<{ monthKey: string; pacing: BudgetPacing[] }> =>
  request('/api/budgets/pacing', { query: monthKey ? { monthKey } : undefined });

export const saveBudget = (input: BudgetUpsert): Promise<{ budget: Budget }> =>
  request('/api/budgets', { method: 'POST', body: input });

export const deleteBudget = (id: string): Promise<{ ok: true }> =>
  request(`/api/budgets/${id}`, { method: 'DELETE' });

export const getBudgetSuggestions = (
  monthKey?: string,
): Promise<{ suggestions: Array<{ categoryId: string; suggestedAmount: number; basedOnMonths: number }> }> =>
  request('/api/budgets/suggestions', { query: monthKey ? { monthKey } : undefined });

// ---------------------------------------------------------------------------
//  Fiado
// ---------------------------------------------------------------------------

export interface FiadoSummaryResponse {
  totalDebt: number;
  overLimit: number;
  monthPurchases: number;
  monthPayments: number;
  monthByPocket: { market: number; snack: number; leisure: number };
  stores: Array<{
    id: string;
    name: string;
    balance: number;
    limit: number | null;
    available: number | null;
    overLimit: boolean;
  }>;
}

export const getFiadoSummary = (): Promise<FiadoSummaryResponse> =>
  request('/api/fiado/summary');

export const getFiadoStores = (): Promise<{ stores: FiadoStore[] }> =>
  request('/api/fiado/stores');

export const createFiadoStore = (input: FiadoStoreCreate): Promise<{ store: FiadoStore }> =>
  request('/api/fiado/stores', { method: 'POST', body: input });

export const updateFiadoStore = (
  id: string,
  input: FiadoStoreUpdate,
): Promise<{ store: FiadoStore }> =>
  request(`/api/fiado/stores/${id}`, { method: 'PUT', body: input });

export const deleteFiadoStore = (id: string): Promise<{ ok: true; message: string }> =>
  request(`/api/fiado/stores/${id}`, { method: 'DELETE' });

export const getFiadoLedger = (id: string): Promise<{ entries: FiadoEntry[] }> =>
  request(`/api/fiado/stores/${id}/ledger`);

// ---------------------------------------------------------------------------
//  Deudas
// ---------------------------------------------------------------------------

export interface DebtsTotals {
  totalBalance: number;
  monthlyInstallments: number;
  activeCount: number;
  frozenCount: number;
  negotiationCount: number;
  byPriority: Array<{ priority: number; balance: number; count: number }>;
}

export const getDebts = (): Promise<{ debts: Debt[] }> => request('/api/debts');

export const getDebtsTotals = (): Promise<DebtsTotals> => request('/api/debts/totals');

export const createDebt = (input: DebtCreate): Promise<{ debt: Debt }> =>
  request('/api/debts', { method: 'POST', body: input });

export const updateDebt = (id: string, input: DebtUpdate): Promise<{ debt: Debt }> =>
  request(`/api/debts/${id}`, { method: 'PUT', body: input });

export const deleteDebt = (id: string): Promise<{ ok: true }> =>
  request(`/api/debts/${id}`, { method: 'DELETE' });

export const payDebtInstallment = (
  debtId: string,
  input: Omit<DebtPaymentCreate, 'debtId'>,
  idempotencyKey?: string,
): Promise<{
  debt: Debt;
  principal: number;
  interest: number;
  balanceBefore: number;
  balanceAfter: number;
  idempotent: boolean;
}> =>
  request(`/api/debts/${debtId}/payment`, {
    method: 'POST',
    body: input,
    ...(idempotencyKey ? { headers: { 'idempotency-key': idempotencyKey } } : {}),
  });

export interface SimulatedMonth {
  month: number;
  monthKey: string;
  interest: number;
  paid: number;
  remaining: number;
}

export interface SimulationOutcome {
  strategy: 'snowball' | 'avalanche';
  months: number | null;
  payoffMonthKey: string | null;
  totalInterest: number;
  totalPaid: number;
  payoffOrder: Array<{ id: string; entity: string; month: number; monthKey: string }>;
  timeline: SimulatedMonth[];
  unpayable: boolean;
}

export interface StrategyComparison {
  snowball: SimulationOutcome;
  avalanche: SimulationOutcome;
  interestSavedByAvalanche: number;
  monthsSavedBySnowball: number;
  recommendation: string;
}

export const simulateDebts = (extra = 0): Promise<StrategyComparison> =>
  request('/api/debts/simulate', { query: { extra } });

// ---------------------------------------------------------------------------
//  Gastos compartidos
// ---------------------------------------------------------------------------

export interface Receivables {
  totalReceivable: number;
  totalSettled: number;
  pendingCount: number;
  byDebtor: Array<{ debtorName: string; amount: number; count: number; oldestDate: string | null }>;
}

export const getSplits = (): Promise<{ splits: Split[] }> => request('/api/splits');

export const getReceivables = (): Promise<Receivables> => request('/api/splits/receivables');

export const createSplit = (input: SplitCreate): Promise<{ split: Split }> =>
  request('/api/splits', { method: 'POST', body: input });

export const settleSplit = (id: string, input: SplitSettle): Promise<{ split: Split }> =>
  request(`/api/splits/${id}/settle`, { method: 'POST', body: input });

export const deleteSplit = (id: string): Promise<{ ok: true }> =>
  request(`/api/splits/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
//  Metas
// ---------------------------------------------------------------------------

export interface GoalsSummary {
  totalTarget: number;
  totalSaved: number;
  remaining: number;
  activeCount: number;
  achievedCount: number;
  requiredMonthly: number;
  monthContributions: number;
}

export const getGoals = (): Promise<{ goals: Goal[] }> => request('/api/goals');

export const getGoalsSummary = (): Promise<GoalsSummary> => request('/api/goals/summary');

export const createGoal = (input: GoalCreate): Promise<{ goal: Goal }> =>
  request('/api/goals', { method: 'POST', body: { icon: 'Target', ...input } });

export const updateGoal = (id: string, input: GoalUpdate): Promise<{ goal: Goal }> =>
  request(`/api/goals/${id}`, { method: 'PUT', body: input });

export const deleteGoal = (id: string): Promise<{ ok: true }> =>
  request(`/api/goals/${id}`, { method: 'DELETE' });

export const contributeToGoal = (
  id: string,
  input: Omit<GoalContributionCreate, 'goalId'>,
): Promise<{ goal: Goal; justAchieved: boolean }> =>
  request(`/api/goals/${id}/contribute`, { method: 'POST', body: input });

export const getGoalContributions = (
  id: string,
): Promise<{ contributions: Array<{ id: string; amount: number; date: string }> }> =>
  request(`/api/goals/${id}/contributions`);

// ---------------------------------------------------------------------------
//  Gastos fijos
// ---------------------------------------------------------------------------

export const getRecurring = (): Promise<{ rules: RecurringRule[] }> => request('/api/recurring');

export const getUpcomingRecurring = (days = 30): Promise<{ rules: RecurringRule[] }> =>
  request('/api/recurring/upcoming', { query: { days } });

export const getPendingRecurring = (): Promise<{ pending: number }> =>
  request('/api/recurring/pending');

/**
 * Borra varios movimientos de una vez.
 *
 * Devuelve cuántos se borraron y cuántos no se encontraron. Se enseñan los dos: si se
 * pidieron diez y solo se borraron siete, el usuario tiene que saberlo. Decir «listo»
 * y callar los otros tres sería mentirle.
 */
export const deleteTransactionsBulk = (
  ids: string[],
): Promise<{ deleted: number; notFound: number }> =>
  request('/api/transactions/bulk/delete', { method: 'POST', body: { ids } });

/** Restaura varios movimientos de la papelera de una vez. */
export const restoreTransactionsBulk = (
  ids: string[],
): Promise<{ restored: number; notFound: number }> =>
  request('/api/transactions/bulk/restore', { method: 'POST', body: { ids } });

/**
 * Un hallazgo del radar de suscripciones.
 *
 * `message` es qué se encontró y `suggestion` qué se puede hacer. Se mandan ya
 * escritos desde el servidor porque el cálculo de la cifra y su explicación van
 * juntos: separarlos es cómo se acaba enseñando una cifra que no corresponde al
 * texto que la acompaña.
 */
export interface RadarFinding {
  kind:
    | 'STOPPED_CHARGING'
    | 'PRICE_INCREASE'
    | 'REPEATEDLY_SKIPPED'
    | 'DUPLICATED'
    | 'LONG_RUNNING';
  ruleId: string;
  description: string;
  amount: number;
  annualImpact: number;
  message: string;
  suggestion: string;
  severity: 'high' | 'medium' | 'low';
}

export interface RadarResult {
  findings: RadarFinding[];
  /** Lo que suman al año los hallazgos, sin contar dos veces la misma suscripción. */
  annualImpact: number;
  /** Lo que suman al año todas las suscripciones activas. */
  totalAnnual: number;
  monthlyTotal: number;
  activeCount: number;
}

/** El radar de suscripciones: lo que ya no usas, lo que subió y lo que está triplicado. */
export const getSubscriptionRadar = (): Promise<RadarResult> =>
  request('/api/recurring/radar');

export const createRecurring = (input: RecurringCreate): Promise<{ rule: RecurringRule }> =>
  request('/api/recurring', { method: 'POST', body: { isPaused: false, ...input } });

export const updateRecurring = (
  id: string,
  input: RecurringUpdate,
): Promise<{ rule: RecurringRule }> => request(`/api/recurring/${id}`, { method: 'PUT', body: input });

export const toggleRecurring = (id: string, paused?: boolean): Promise<{ rule: RecurringRule }> =>
  request(`/api/recurring/${id}/toggle`, { method: 'POST', body: { paused } });

export const deleteRecurring = (id: string): Promise<{ ok: true }> =>
  request(`/api/recurring/${id}`, { method: 'DELETE' });

/** Ejecuta los cobros pendientes. Es idempotente. */
export const runRecurring = (): Promise<{
  created: number;
  skipped: number;
  entries: Array<{ ruleId: string; description: string; date: string; amount: number }>;
}> => request('/api/recurring/run', { method: 'POST' });

// ---------------------------------------------------------------------------
//  Informes
// ---------------------------------------------------------------------------

export interface FinancialSummary {
  monthKey: string;
  availableCash: number;
  reservedForGoals: number;
  spendable: number;
  netWorth: number;
  assets: number;
  liabilities: number;
  debtsTotal: number;
  fiadoTotal: number;
  creditCardDebt: number;
  monthExpense: number;
  monthIncome: number;
  cashIn: number;
  cashOut: number;
  cashFlow: number;
  previous: { monthKey: string; expense: number; income: number } | null;
  expenseChangePercent: number | null;
}

export interface CategoryBreakdownRow {
  categoryId: string | null;
  name: string;
  color: string;
  total: number;
  percentOfTotal: number;
  previousTotal: number;
  changePercent: number | null;
}

export interface SeriesPoint {
  monthKey: string;
  expense: number;
  income: number;
  net: number;
}

export interface BudgetPacingRow {
  categoryId: string;
  name: string;
  available: number;
  spent: number;
  remaining: number;
  percentUsed: number;
  onTrackToExceed: boolean;
  daysUntilExhausted: number | null;
}

export interface Projection {
  startingBalance: number;
  months: Array<{
    monthKey: string;
    committed: number;
    estimatedVariable: number;
    estimatedIncome: number;
    projectedBalance: number;
  }>;
  firstNegativeMonth: string | null;
  disclaimer: string;
}

export interface HormigaAudit {
  monthKey: string;
  hormigaSpent: number;
  annualProjection: number;
  dailyBudget: number;
  dailyAverage: number;
  daysOverBudget: number;
  comparison: { label: string; unitValue: number; units: number } | null;
}

/**
 * El rango de fechas que se manda a los informes.
 *
 * Se manda siempre que el período no sea un mes: es lo que permite ver un día suelto,
 * un año entero o unas vacaciones concretas. El servidor respeta el rango y compara
 * contra el tramo anterior de la misma duración.
 */
export interface ReportRange {
  from: string;
  to: string;
}

export const getSummary = (
  monthKey?: string,
  range?: ReportRange | undefined,
): Promise<FinancialSummary> =>
  request('/api/reports/summary', {
    query: {
      ...(monthKey ? { monthKey } : {}),
      ...(range ? { from: range.from, to: range.to } : {}),
    },
  });

export const getCategoryBreakdown = (
  monthKey?: string,
  range?: ReportRange | undefined,
): Promise<{ categories: CategoryBreakdownRow[] }> =>
  request('/api/reports/categories', {
    query: {
      ...(monthKey ? { monthKey } : {}),
      ...(range ? { from: range.from, to: range.to } : {}),
    },
  });

export const getSeries = (months = 12, monthKey?: string): Promise<{ series: SeriesPoint[] }> =>
  request('/api/reports/series', { query: { months, ...(monthKey ? { monthKey } : {}) } });

/**
 * La racha de ahorro.
 *
 * `pendingMonth` es el mes en curso cuando todavía no se puede juzgar: ni suma ni
 * rompe. Está ahí para poder explicarlo, y para que el usuario no vea una racha que
 * no cuadra con lo que él mismo está viendo en su mes.
 */
export interface StreakResult {
  current: number;
  best: number;
  startMonth: string | null;
  savedDuringStreak: number;
  pendingMonth: string | null;
  brokenBy: { monthKey: string; gap: number } | null;
}

export const getStreak = (monthKey?: string): Promise<StreakResult> =>
  request('/api/reports/streak', { query: monthKey ? { monthKey } : {} });

/** Lo que se restauró de una copia de seguridad. */
export interface RestoreResultDto {
  mode: 'replace' | 'merge';
  restored: Record<string, number>;
  /** Registros rotos o incompletos que se descartaron. */
  skipped: number;
  /** Categorías del sistema que ya existían. No son un error. */
  alreadyPresent: number;
  exportedAt: string | null;
}

/**
 * Restaura una copia de seguridad.
 *
 * `merge` conserva lo que hay y añade; `replace` borra lo actual y pone lo del archivo.
 * La decisión es del usuario y no del programa: restaurar puede destruir meses de
 * trabajo, y eso no lo decide una heurística.
 */
export const restoreBackup = (
  backup: Record<string, unknown>,
  mode: 'replace' | 'merge',
): Promise<RestoreResultDto> =>
  request('/api/reports/restore', { method: 'POST', body: { backup, mode } });

export const getReportBudgets = (
  monthKey?: string,
): Promise<{ budgets: BudgetPacingRow[] }> =>
  request('/api/reports/budgets', { query: monthKey ? { monthKey } : undefined });

export const getProjection = (months = 6, monthKey?: string): Promise<Projection> =>
  request('/api/reports/projection', { query: { months, ...(monthKey ? { monthKey } : {}) } });

export const getHormigaAudit = (
  monthKey?: string,
  value?: number,
  comparison?: string,
): Promise<HormigaAudit> =>
  request('/api/reports/hormiga', {
    query: {
      ...(monthKey ? { monthKey } : {}),
      ...(value !== undefined ? { value } : {}),
      ...(comparison ? { comparison } : {}),
    },
  });

/** Enlace de descarga del CSV. No hace falta pedirlo: lo abre el navegador. */
export const exportCsvUrl = (monthKey?: string): string =>
  `/api/reports/export.csv${monthKey ? `?monthKey=${monthKey}` : ''}`;

export const exportJsonUrl = (): string => '/api/reports/export.json';

export const purgeAllData = (): Promise<{ deleted: Record<string, number> }> =>
  request('/api/reports/purge', { method: 'POST', body: { confirmation: 'BORRAR' } });

// ---------------------------------------------------------------------------
//  Sincronización sin conexión
// ---------------------------------------------------------------------------

export interface SyncStatusResponse {
  serverTime: string;
  today: string;
  counts: Record<string, number>;
}

export const getSyncStatus = (): Promise<SyncStatusResponse> => request('/api/sync/status');

/** Trae todo lo que cambió desde el cursor. */
export const syncPull = (since?: string): Promise<SyncPullResult> =>
  request('/api/sync/pull', { query: since ? { since } : undefined });

/** Envía un lote de operaciones hechas sin conexión. */
export const syncPush = (
  operations: readonly unknown[],
): Promise<{ results: SyncOperationResult[]; applied: number; failed: number; cursor: string }> =>
  request('/api/sync/push', { method: 'POST', body: { operations } });

// ---------------------------------------------------------------------------
//  Importación de extractos
// ---------------------------------------------------------------------------

/** Lee el archivo y propone el mapeo, sin tocar nada. */
export const importPreview = (
  content: string,
  hasHeader = true,
): Promise<{
  headers: string[];
  mapping: string[];
  totalRows: number;
  validRows: number;
  problemRows: number;
  rows: Array<{
    line: number;
    date: string | null;
    amount: number | null;
    description: string;
    balance: number | null;
    problems: string[];
  }>;
  hasHeader: boolean;
}> => request('/api/import/preview', { method: 'POST', body: { content, hasHeader } });

export const importCommit = (input: {
  content: string;
  hasHeader: boolean;
  mapping: Array<'date' | 'amount' | 'description' | 'debit' | 'credit' | 'balance' | 'ignore'>;
  accountId: string;
  defaultCategoryId?: string | undefined;
  skipDuplicates?: boolean;
  fileName?: string | undefined;
}): Promise<{
  batchId: string;
  accountName: string;
  totalRows: number;
  imported: number;
  duplicates: number;
  problems: number;
  problemsDetail: Array<{ line: number; problems: string[] }>;
}> => request('/api/import/commit', { method: 'POST', body: input });

export const getImportBatches = (): Promise<{
  batches: Array<{
    id: string;
    fileName: string | null;
    accountId: string | null;
    accountName: string | null;
    totalRows: number;
    imported: number;
    duplicates: number;
    problems: number;
    status: 'APPLIED' | 'UNDONE';
    undoneAt: string | null;
    createdAt: string;
  }>;
}> => request('/api/import/batches');

export const undoImport = (
  batchId: string,
): Promise<{ deleted: number; kept: number }> =>
  request(`/api/import/batches/${batchId}/undo`, { method: 'POST' });

// ---------------------------------------------------------------------------
//  Entrar con la huella (WebAuthn)
// ---------------------------------------------------------------------------

/** Una credencial vinculada, tal como llega del servidor. */
export interface WebAuthnCredentialDto {
  id: string;
  label: string;
  deviceType: string;
  backedUp: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

/**
 * Pide las opciones para vincular una huella.
 *
 * El **desafío** que va dentro lo guarda el servidor en una cookie `HttpOnly` y lo
 * comprueba al terminar. Aquí no se toca: solo viaja al dispositivo para que lo firme.
 */
export const getWebAuthnRegisterOptions = (
  label: string,
): Promise<{ options: PublicKeyCredentialCreationOptionsJSON }> =>
  request('/api/auth/webauthn/register/options', { method: 'POST', body: { label } });

/** Manda la respuesta firmada por el dispositivo para terminar el vínculo. */
export const verifyWebAuthnRegistration = (
  response: unknown,
  label: string,
): Promise<{ credentialId: string; label: string }> =>
  request('/api/auth/webauthn/register/verify', { method: 'POST', body: { response, label } });

/** Pide las opciones para entrar. Sin correo: el dispositivo dice qué credenciales tiene. */
export const getWebAuthnLoginOptions = (
  email?: string,
): Promise<{ options: PublicKeyCredentialRequestOptionsJSON }> =>
  request('/api/auth/webauthn/login/options', {
    method: 'POST',
    body: email ? { email } : {},
  });

/** Manda la firma y recibe la sesión, igual que si hubiera escrito la contraseña. */
export const verifyWebAuthnLogin = (
  response: unknown,
): Promise<{ accessToken: string; expiresIn: number; user: UserPublic }> =>
  request('/api/auth/webauthn/login/verify', { method: 'POST', body: { response } });

/** Las credenciales vinculadas a esta cuenta. */
export const getWebAuthnCredentials = (): Promise<{ credentials: WebAuthnCredentialDto[] }> =>
  request('/api/auth/webauthn/credentials');

/** Borra una credencial. Es lo que se hace al perder el teléfono. */
export const deleteWebAuthnCredential = (id: string): Promise<{ ok: true }> =>
  request(`/api/auth/webauthn/credentials/${id}`, { method: 'DELETE' });

/*
 * Los tipos del estándar WebAuthn. Se declaran aquí, en versión mínima, para no
 * depender de los tipos del DOM que cambian entre versiones de TypeScript. Lo que
 * importa es que el objeto viaja tal cual al dispositivo y vuelve firmado.
 */
export interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  [key: string]: unknown;
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  rpId?: string;
  allowCredentials?: Array<{ type: 'public-key'; id: string; transports?: string[] }>;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
//  Recibos
// ---------------------------------------------------------------------------

export interface ReceiptItemDto {
  description: string;
  amount: number;
  categoryId: string | null;
  /**
   * De dónde salió la categoría:
   *
   *  · `MEMORY` — la aplicación la recuerda porque tú se la enseñaste.
   *  · `GUESS`  — la adivinó por el nombre del producto. Puede equivocarse.
   *  · `NONE`   — no se sabe. Hay que preguntarte.
   */
  source: 'MEMORY' | 'GUESS' | 'NONE';
  confidence: number;
}

export interface ReceiptDto {
  id: string;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  merchant: string | null;
  amount: number | null;
  confidence: number | null;
  items: ReceiptItemDto[];
  rawText: string | null;
  warning: string | null;
  engine: string | null;
  confirmedAt: string | null;
  createdAt: string;
  imageUrl: string;
}

export interface ReceiptSuggestionDto {
  merchant: string | null;
  amount: number | null;
  confidence: number;
  items: ReceiptItemDto[];
  warning: string | null;
  rawText: string;
}

export const getReceipts = (): Promise<{
  receipts: ReceiptDto[];
  pending: number;
  usedBytes: number;
}> => request('/api/receipts');
/**
 * Interpreta el texto de un recibo.
 *
 * Recibe **texto**, no la imagen: la lectura de la foto ocurre en el dispositivo, así
 * que la imagen nunca sale del teléfono para ser interpretada.
 */
export const suggestReceiptItems = (hintText: string): Promise<ReceiptSuggestionDto> =>
  request('/api/receipts/suggest', { method: 'POST', body: { hintText } });

/** Sube la foto del recibo. */
export const uploadReceipt = (input: {
  contentBase64: string;
  mimeType: string;
  fileName?: string | undefined;
  merchant?: string | undefined;
  amount?: number | undefined;
  items?: ReceiptItemDto[] | undefined;
  rawText?: string | undefined;
  engine?: string | undefined;
  confidence?: number | undefined;
}): Promise<{ receipt: ReceiptDto }> =>
  request('/api/receipts', { method: 'POST', body: input });

/** Confirma el recibo revisado y crea el movimiento. */
export const confirmReceipt = (
  id: string,
  input: {
    kind: string;
    amount: number;
    date: string;
    time?: string;
    accountId?: string | undefined;
    categoryId?: string | undefined;
    merchant?: string | undefined;
    notes?: string | undefined;
    items?: Array<{
      description: string;
      amount: number;
      categoryId?: string | null | undefined;
    }>;
  },
  idempotencyKey?: string,
): Promise<{ receipt: ReceiptDto; transaction: Transaction }> =>
  request(`/api/receipts/${id}/confirm`, {
    method: 'POST',
    body: input,
    ...(idempotencyKey ? { headers: { 'idempotency-key': idempotencyKey } } : {}),
  });

export const deleteReceipt = (id: string): Promise<{ ok: true }> =>
  request(`/api/receipts/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
//  Copiloto
// ---------------------------------------------------------------------------

export interface CopilotAnswerResponse {
  question: string;
  understood: boolean;
  answer: string;
  period: string;
  intent: string;
  sources: Array<{ label: string; value: number; detail?: string }>;
  suggestions: string[];
}

/** Lo que el copiloto sabe responder. */
export const getCopilotCapabilities = (): Promise<{ examples: string[]; explanation: string }> =>
  request('/api/copilot/capabilities');

/** Pregunta al copiloto. */
export const askCopilot = (question: string): Promise<CopilotAnswerResponse> =>
  request('/api/copilot/ask', { method: 'POST', body: { question } });

// ---------------------------------------------------------------------------
//  Ajustes
// ---------------------------------------------------------------------------

const zSettingsEnvelope = z.object({ settings: z.object({}).passthrough() });

export const getSettings = (): Promise<{ settings: Settings }> =>
  request('/api/settings', { schema: zSettingsEnvelope }) as Promise<{ settings: Settings }>;

export const saveSettings = (input: SettingsUpdate): Promise<{ settings: Settings }> =>
  request('/api/settings', { method: 'PUT', body: input });
